import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import SchemaMarkup from '../../../components/feature/SchemaMarkup';
import RelatedBlogPosts from '../../../components/feature/RelatedBlogPosts';

export default function IPCameraSystemsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // SEO Meta Tags
    document.title = "IP Kamera Sistemleri Kurulumu İstanbul | 4K Ultra HD Kamera";
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'İstanbul, Kocaeli, Gebze, Tuzla ve Bursa\'da profesyonel IP kamera sistemleri kurulumu. 4K Ultra HD görüntü, gece görüş, uzaktan izleme ve akıllı algılama özellikleri ile 7/24 güvenlik.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'İstanbul, Kocaeli, Gebze, Tuzla ve Bursa\'da profesyonel IP kamera sistemleri kurulumu. 4K Ultra HD görüntü, gece görüş, uzaktan izleme ve akıllı algılama özellikleri ile 7/24 güvenlik.';
      document.head.appendChild(meta);
    }

    // Keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'IP kamera kurulumu, güvenlik kamerası, 4K kamera sistemi, fabrika kamera kurulumu, CCTV kurulumu');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'IP kamera kurulumu, güvenlik kamerası, 4K kamera sistemi, fabrika kamera kurulumu, CCTV kurulumu';
      document.head.appendChild(meta);
    }

    // Open Graph Tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'IP Kamera Sistemleri Kurulumu İstanbul | 4K Ultra HD Kamera');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'IP Kamera Sistemleri Kurulumu İstanbul | 4K Ultra HD Kamera';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Fabrika ve sanayi tesisleri için 4K Ultra HD IP kamera kurulumu. Uzaktan izleme, hareket algılama ve 7/24 kayıt sistemi.');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Fabrika ve sanayi tesisleri için 4K Ultra HD IP kamera kurulumu. Uzaktan izleme, hareket algılama ve 7/24 kayıt sistemi.';
      document.head.appendChild(meta);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://fiberek.com/guvenlik-sistemleri/ip-kamera-sistemleri');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      meta.content = 'https://fiberek.com/guvenlik-sistemleri/ip-kamera-sistemleri';
      document.head.appendChild(meta);
    }

    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://fiberek.com/guvenlik-sistemleri/ip-kamera-sistemleri');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://fiberek.com/guvenlik-sistemleri/ip-kamera-sistemleri';
      document.head.appendChild(link);
    }

    // Cleanup function
    return () => {
      document.title = 'Fiber Ek';
    };
  }, []);

  const features = [
    { icon: 'ri-hd-line', title: '4K Ultra HD Görüntü', description: 'Kristal netliğinde görüntü kalitesi' },
    { icon: 'ri-moon-line', title: 'Gece Görüş', description: '100 metreye kadar IR görüş mesafesi' },
    { icon: 'ri-smartphone-line', title: 'Uzaktan İzleme', description: 'Mobil ve web üzerinden canlı izleme' },
    { icon: 'ri-notification-3-line', title: 'Akıllı Algılama', description: 'Hareket, yüz ve plaka tanıma' },
    { icon: 'ri-cloud-line', title: 'Bulut Depolama', description: 'Güvenli bulut kayıt sistemi' },
    { icon: 'ri-shield-check-line', title: 'Siber Güvenlik', description: 'Şifreli veri aktarımı' }
  ];

  const applications = [
    'Fabrika ve Sanayi Tesisleri',
    'Üretim Hatları İzleme',
    'Depo ve Lojistik Alanları',
    'Ofis ve İdari Binalar',
    'Otopark Güvenliği',
    'Çevre Güvenlik İzleme',
    'Giriş-Çıkış Kontrol Noktaları',
    'Kritik Altyapı İzleme'
  ];

  const cameraTypes = [
    {
      title: 'Dome Kameralar',
      description: 'İç mekan kullanımı için ideal, vandalizme dayanıklı tasarım. 360 derece dönebilen PTZ modelleri mevcuttur.',
      image: 'https://public.readdy.ai/ai/img_res/e24a8c8af577abf9ca4c7a688729b8af.jpg'
    },
    {
      title: 'Bullet Kameralar',
      description: 'Dış mekan için dayanıklı, uzun menzilli görüş. IP67 su geçirmez koruma sınıfı ile her hava koşulunda çalışır.',
      image: 'https://readdy.ai/api/search-image?query=professional%20white%20bullet%20security%20camera%20outdoor%20surveillance%20equipment%20weatherproof%20IP%20camera%20with%20infrared%20night%20vision%20long%20range%20monitoring%20device%20clean%20white%20background%20product%20photography%20industrial%20grade%20camera&width=600&height=400&seq=sec003&orientation=landscape'
    },
    {
      title: 'PTZ Kameralar',
      description: 'Pan-Tilt-Zoom özellikli, geniş alan kontrolü. 360 derece dönme, 20x optik zoom ve oto takip özellikleri.',
      image: 'https://readdy.ai/api/search-image?query=professional%20white%20PTZ%20security%20camera%20pan%20tilt%20zoom%20surveillance%20equipment%20high%20speed%20dome%20camera%20with%20optical%20zoom%20lens%20clean%20white%20background%20product%20photography%20advanced%20monitoring%20technology&width=600&height=400&seq=sec004&orientation=landscape'
    }
  ];

  const faqs = [
    {
      question: 'IP kamera sistemleri nasıl çalışır?',
      answer: 'IP kamera sistemleri, görüntüleri dijital formatta ağ üzerinden ileterek çalışır. Kameralar, ethernet kablosu veya Wi-Fi üzerinden NVR (Network Video Recorder) cihazına veya doğrudan bulut sunucularına bağlanır. Görüntüler yüksek çözünürlükte kaydedilir ve mobil cihazlar veya bilgisayarlar üzerinden canlı olarak izlenebilir. Modern IP kamera sistemleri, hareket algılama, yüz tanıma ve plaka okuma gibi akıllı özellikler sunar.'
    },
    {
      question: 'IP kamera sistemleri kurulumu ne kadar sürer?',
      answer: 'Kurulum süresi, kamera sayısına ve projenin büyüklüğüne bağlıdır. Küçük ölçekli projeler (4-8 kamera) genellikle 1-2 gün içinde tamamlanır. Orta ölçekli fabrika ve sanayi tesisleri (16-32 kamera) 3-5 gün, büyük ölçekli kurumsal projeler ise 1-2 hafta sürebilir. Kurulum öncesi keşif yapılır, kablo altyapısı hazırlanır, kameralar monte edilir, NVR yapılandırması yapılır ve sistem testleri gerçekleştirilir.'
    },
    {
      question: 'Hangi kamera çözünürlüğünü seçmeliyim?',
      answer: 'Kamera çözünürlüğü, izleme ihtiyacınıza göre belirlenir. Genel alan izleme için 2MP (1080p) yeterlidir. Detaylı görüntü gereken alanlarda (giriş-çıkış noktaları, kasa alanları) 4MP veya 5MP tercih edilir. Plaka okuma ve yüz tanıma gerektiren uygulamalarda 8MP (4K) kameralar önerilir. Fabrika ve sanayi tesislerinde genellikle 4MP-5MP kameralar optimum performans sağlar.'
    },
    {
      question: 'IP kamera sistemleri ne kadar geriye dönük kayıt yapar?',
      answer: 'Kayıt süresi, hard disk kapasitesine, kamera sayısına, çözünürlüğe ve FPS ayarlarına bağlıdır. Standart kurulumda 1-2 haftalık kayıt yapılır. Daha uzun süre için hard disk kapasitesi artırılabilir. Örneğin, 16 kamera için 4TB hard disk yaklaşık 15 gün, 8TB hard disk 30 gün kayıt sağlar. Kritik alanlarda bulut yedekleme ile sınırsız arşivleme mümkündür.'
    },
    {
      question: 'IP kamera sistemleri mobil cihazlardan izlenebilir mi?',
      answer: 'Evet, tüm IP kamera sistemlerimiz mobil uyumludur. iOS ve Android cihazlar için ücretsiz mobil uygulamalar mevcuttur. İnternet bağlantısı olan her yerden canlı görüntü izleyebilir, kayıtları geri sarabilir, kamera açılarını kontrol edebilir ve bildirim alabilirsiniz. Ayrıca web tarayıcısı üzerinden de sisteme erişim sağlanabilir.'
    },
    {
      question: 'Hangi bölgelerde IP kamera sistemleri kurulumu yapıyorsunuz?',
      answer: 'İstanbul Avrupa Yakası, İstanbul Anadolu Yakası, Tuzla, Gebze, Dilovası, Çayırova, Kocaeli, Sakarya, Yalova, Bursa, Tekirdağ ve Çorlu bölgelerinde IP kamera sistemleri kurulumu hizmeti veriyoruz. Fabrika, sanayi tesisi, OSB ve kurumsal işletmelere özel çözümler sunuyoruz. Ücretsiz keşif ve teknik danışmanlık için bizi arayabilirsiniz.'
    }
  ];

  // Schema Markup Data
  const localBusinessSchema = {
    name: "Fiber Ek - IP Kamera Sistemleri",
    description: "İstanbul, Kocaeli, Gebze, Tuzla ve Bursa'da profesyonel IP kamera sistemleri kurulumu. 4K Ultra HD görüntü, gece görüş, uzaktan izleme ve akıllı algılama özellikleri.",
    url: "https://fiberek.com/guvenlik-sistemleri/ip-kamera-sistemleri",
    telephone: "+90 553 716 55 61",
    address: {
      locality: "İstanbul",
      region: "İstanbul",
      country: "TR"
    },
    geo: {
      latitude: "40.9928",
      longitude: "29.1244"
    },
    openingHours: ["Mo-Fr 08:00-18:00", "Sa 09:00-16:00"],
    priceRange: "$$",
    areaServed: ["İstanbul", "Kocaeli", "Gebze", "Tuzla", "Bursa", "Sakarya", "Yalova", "Tekirdağ"],
    serviceType: "IP Kamera Sistemleri Kurulumu",
    hasCredential: "TSE ve CE Onaylı Kurulum",
    sameAs: ["https://wa.me/905537165561"]
  };

  const serviceSchema = {
    name: "IP Kamera Sistemleri Kurulumu",
    description: "Fabrika, sanayi tesisi ve kurumsal işletmeler için 4K Ultra HD IP kamera sistemleri kurulumu. Uzaktan izleme, hareket algılama ve 7/24 kayıt sistemi.",
    provider: {
      name: "Fiber Ek",
      telephone: "+90 553 716 55 61",
      url: "https://fiberek.com",
      address: {
        locality: "İstanbul",
        region: "İstanbul",
        country: "TR"
      }
    },
    areaServed: ["İstanbul Avrupa", "İstanbul Anadolu", "Kocaeli", "Gebze", "Tuzla", "Bursa"],
    serviceType: "IP Kamera Kurulumu",
    priceRange: "2000-30000 TRY",
    catalog: {
      name: "IP Kamera Sistemleri",
      services: [
        {
          name: "Dome IP Kamera Kurulumu",
          description: "İç mekan için vandalizme dayanıklı dome kamera kurulumu"
        },
        {
          name: "Bullet IP Kamera Kurulumu",
          description: "Dış mekan için weatherproof bullet kamera kurulumu"
        },
        {
          name: "PTZ IP Kamera Kurulumu",
          description: "360 derece dönebilen ve zoom yapabilen PTZ kamera kurulumu"
        },
        {
          name: "NVR Sistem Kurulumu",
          description: "Network Video Recorder ile kayıt sistemi kurulumu"
        }
      ]
    }
  };

  const faqSchema = {
    faqs: faqs
  };

  const breadcrumbSchema = {
    breadcrumbs: [
      { name: "Ana Sayfa", url: "https://fiberek.com/" },
      { name: "Güvenlik Sistemleri", url: "https://fiberek.com/guvenlik-sistemleri" },
      { name: "IP Kamera Sistemleri", url: "https://fiberek.com/guvenlik-sistemleri/ip-kamera-sistemleri" }
    ]
  };

  // Related Blog Posts Data
  const relatedBlogPosts = [
    {
      title: "IP Kamera Seçim Rehberi: Fabrika ve Sanayi Tesisleri İçin",
      slug: "ip-kamera-secim-rehberi",
      description: "Fabrika ve sanayi tesisleri için doğru IP kamera seçimi nasıl yapılır? 4K çözünürlük, gece görüş, PoE teknolojisi ve kurulum önerileri.",
      category: "Güvenlik Sistemleri",
      readTime: "8 dakika"
    },
    {
      title: "Sanayi Bölgesi Kamera Sistemi Kurulum Projesi",
      slug: "sanayi-bolgesi-kamera-sistemi",
      description: "OSB ve sanayi bölgelerinde IP kamera sistemleri nasıl kurulur? Geniş alan kapsama, fiber altyapı entegrasyonu ve 7/24 izleme çözümleri.",
      category: "Proje Deneyimleri",
      readTime: "12 dakika"
    },
    {
      title: "Fabrika Fiber Altyapı Rehberi: Kamera ve Güvenlik Sistemleri",
      slug: "fabrikalarda-fiber-altyapi-rehberi",
      description: "Fabrikalar için fiber optik altyapı nasıl planlanır? Kamera sistemleri, güvenlik altyapısı ve endüstriyel ağ çözümleri rehberi.",
      category: "Fiber Optik",
      readTime: "10 dakika"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Schema Markup Components */}
      <SchemaMarkup type="LocalBusiness" data={localBusinessSchema} />
      <SchemaMarkup type="Service" data={serviceSchema} />
      <SchemaMarkup type="FAQPage" data={faqSchema} />
      <SchemaMarkup type="BreadcrumbList" data={breadcrumbSchema} />

      <Navbar />
      <FloatingButtons />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20IP%20security%20camera%20installation%20in%20industrial%20facility%20professional%20surveillance%20system%20with%20multiple%20cameras%20monitoring%20factory%20production%20line%20high%20tech%20security%20infrastructure%20with%20blue%20ambient%20lighting%20dark%20background%20cinematic%20industrial%20environment&width=1920&height=800&seq=sec005&orientation=landscape"
            alt="IP Kamera Sistemleri"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/85 to-slate-950"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
            <Link to="/" className="hover:text-red-400 transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/guvenlik-sistemleri" className="hover:text-red-400 transition-colors cursor-pointer">Güvenlik Sistemleri</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-red-400">IP Kamera Sistemleri</span>
          </nav>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600/20 border border-red-500/30 rounded-full mb-6">
              <i className="ri-camera-line text-red-400 text-lg"></i>
              <span className="text-red-300 text-sm font-medium">Profesyonel Kamera Sistemleri</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              IP Kamera Sistemleri Kurulumu
            </h1>

            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              İstanbul, Kocaeli, Gebze, Tuzla, Bursa ve çevre illerde fabrika, sanayi tesisi ve kurumsal işletmeler için profesyonel IP kamera sistemleri kurulumu. 4K Ultra HD görüntü kalitesi, gece görüş, uzaktan izleme ve akıllı algılama özellikleri ile 7/24 güvenlik.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="tel:+905537165561"
                className="px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                +90 553 716 55 61
              </a>
              <a
                href="https://wa.me/905537165561"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-slate-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <article className="prose prose-invert prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-white mb-6">IP Kamera Sistemleri Nedir?</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                IP kamera sistemleri, görüntüleri dijital formatta ağ üzerinden ileten modern güvenlik kameralarıdır. Geleneksel analog kameralara göre çok daha yüksek çözünürlük, gelişmiş özellikler ve esnek kurulum imkanı sunar. IP kamera sistemleri, ethernet kablosu veya Wi-Fi üzerinden NVR (Network Video Recorder) cihazına veya doğrudan bulut sunucularına bağlanarak çalışır. Fabrika, sanayi tesisi, ofis ve kurumsal işletmelerde güvenlik izleme için tercih edilen en modern çözümdür.
              </p>
              <p className="text-gray-300 leading-relaxed mb-8">
                İstanbul, Kocaeli, Gebze, Tuzla, Bursa ve çevre illerde IP kamera sistemleri kurulumu hizmeti veriyoruz. Profesyonel ekibimiz, keşif aşamasından kurulum ve devreye almaya kadar tüm süreci yönetir. TSE ve CE onaylı ekipmanlar kullanarak, yüksek kaliteli ve güvenilir IP kamera sistemleri kurulumu gerçekleştiriyoruz.
              </p>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">IP Kamera Sistemlerinin Avantajları</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 not-prose">
                {features.map((feature, index) => (
                  <div key={index} className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-red-500/50 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-600 to-orange-600 rounded-lg mb-4">
                      <i className={`${feature.icon} text-2xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">IP Kamera Türleri ve Özellikleri</h2>
              <div className="space-y-8 mb-8 not-prose">
                {cameraTypes.map((camera, index) => (
                  <div key={index} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="w-full h-64">
                        <img
                          src={camera.image}
                          alt={camera.title}
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                      <div className="p-6 flex flex-col justify-center">
                        <h3 className="text-2xl font-bold text-white mb-4">{camera.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{camera.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">IP Kamera Sistemleri Kurulum Süreci</h2>
              <div className="space-y-6 mb-8 not-prose">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-600 to-orange-600 rounded-full text-white font-bold">1</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Keşif ve Planlama</h3>
                    <p className="text-gray-300">Tesiste keşif yapılır, kamera konumları belirlenir, kablo güzergahları planlanır. Müşteri ihtiyaçlarına göre kamera türleri ve sayısı tespit edilir.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-600 to-orange-600 rounded-full text-white font-bold">2</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Altyapı Hazırlığı</h3>
                    <p className="text-gray-300">Kablo kanalları döşenir, Cat6 veya fiber optik kablolar çekilir. PoE switch ve NVR cihazları için uygun konum hazırlanır.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-600 to-orange-600 rounded-full text-white font-bold">3</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Kamera Montajı</h3>
                    <p className="text-gray-300">Kameralar belirlenen noktalara profesyonelce monte edilir. Açı ayarları yapılır, kablo bağlantıları tamamlanır.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-600 to-orange-600 rounded-full text-white font-bold">4</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Sistem Yapılandırması</h3>
                    <p className="text-gray-300">NVR cihazı yapılandırılır, kameralar sisteme eklenir, kayıt ayarları optimize edilir. Uzaktan erişim için gerekli ayarlar yapılır.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-600 to-orange-600 rounded-full text-white font-bold">5</div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Test ve Eğitim</h3>
                    <p className="text-gray-300">Sistem testleri yapılır, görüntü kalitesi kontrol edilir. Müşteriye sistem kullanımı hakkında detaylı eğitim verilir.</p>
                  </div>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">IP Kamera Sistemleri Uygulama Alanları</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 not-prose">
                {applications.map((app, index) => (
                  <div key={index} className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-red-500/50 transition-all duration-300">
                    <i className="ri-checkbox-circle-fill text-red-400 text-xl"></i>
                    <span className="text-gray-300 font-medium">{app}</span>
                  </div>
                ))}
              </div>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">Kullandığımız Ekipmanlar</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                IP kamera sistemleri kurulumlarında dünya çapında tanınmış ve güvenilir markaların ürünlerini kullanıyoruz. Hikvision, Dahua, Axis Communications, Uniview ve Hanwha gibi sektör lideri markaların TSE ve CE onaylı ürünleri ile kurulum yapıyoruz. Tüm ekipmanlar minimum 2 yıl garanti kapsamındadır ve yedek parça temini garantilidir.
              </p>
              <p className="text-gray-300 leading-relaxed mb-8">
                Kamera sistemlerimizde H.265+ video sıkıştırma teknolojisi kullanılarak, daha az bant genişliği ve depolama alanı ile yüksek kaliteli görüntü elde edilir. PoE (Power over Ethernet) teknolojisi sayesinde tek kablo ile hem veri hem de güç iletimi sağlanır. NVR cihazlarımız RAID destekli hard disk yapılandırması ile veri güvenliği sağlar.
              </p>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">Akıllı Özellikler ve Entegrasyonlar</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                Modern IP kamera sistemlerimiz, yapay zeka destekli akıllı özellikler sunar. Hareket algılama, çizgi geçiş tespiti, bölge ihlali algılama, yüz tanıma ve plaka okuma gibi gelişmiş analizler yapılabilir. Kameralar, alarm sistemleri, geçiş kontrol sistemleri ve bariyer sistemleri ile entegre çalışarak kapsamlı bir güvenlik ekosistemi oluşturur.
              </p>
              <p className="text-gray-300 leading-relaxed mb-8">
                Fabrika ve sanayi tesislerinde üretim hatlarının izlenmesi, kalite kontrol süreçlerinin kaydedilmesi ve iş güvenliği takibi için IP kamera sistemleri kullanılır. Isı haritası analizi, insan sayma ve yoğunluk analizi gibi özellikler ile operasyonel verimlilik artırılabilir. Tüm veriler şifreli olarak iletilir ve güvenli bir şekilde saklanır.
              </p>

              <h2 className="text-3xl font-bold text-white mb-6 mt-12">Bakım ve Destek Hizmetleri</h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                IP kamera sistemleri kurulumu sonrasında düzenli bakım ve destek hizmeti sunuyoruz. Kamera lenslerinin temizliği, yazılım güncellemeleri, hard disk kontrolü ve sistem optimizasyonu gibi işlemler periyodik olarak gerçekleştirilir. 7/24 teknik destek hattımız ile her türlü sorun anında çözülür.
              </p>
              <p className="text-gray-300 leading-relaxed mb-8">
                Arızalı kamera değişimi, NVR cihazı bakımı, hard disk yükseltmesi ve sistem genişletme gibi hizmetler de sunuyoruz. İstanbul Avrupa Yakası, İstanbul Anadolu Yakası, Tuzla, Gebze, Kocaeli, Bursa ve diğer bölgelerde hızlı servis hizmeti veriyoruz. Acil durumlarda aynı gün müdahale garantisi sağlıyoruz.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Sıkça Sorulan Sorular
            </h2>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden hover:border-red-500/50 transition-all duration-300">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <h3 className="text-xl font-bold text-white pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-2xl text-red-400 group-open:rotate-180 transition-transform duration-300"></i>
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <RelatedBlogPosts posts={relatedBlogPosts} />

      {/* Related Services */}
      <section className="py-20 bg-slate-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Diğer Güvenlik Sistemleri Hizmetlerimiz
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link to="/guvenlik-sistemleri/hirsiz-alarm-sistemleri" className="group bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-orange-600 to-amber-600 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-alarm-warning-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-orange-400 transition-colors">Hırsız Alarm Sistemleri</h3>
                <p className="text-gray-400 text-sm">Kablosuz ve kablolu alarm sistemleri kurulumu</p>
              </Link>

              <Link to="/guvenlik-sistemleri/yangin-alarm-sistemleri" className="group bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-red-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-red-700 to-red-600 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-fire-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-400 transition-colors">Yangın Alarm Sistemleri</h3>
                <p className="text-gray-400 text-sm">TSE onaylı yangın algılama sistemleri</p>
              </Link>

              <Link to="/guvenlik-sistemleri/gecis-kontrol-sistemleri" className="group bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg mb-4 group-hover:scale-110 transition-transform">
                  <i className="ri-door-lock-line text-2xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">Geçiş Kontrol Sistemleri</h3>
                <p className="text-gray-400 text-sm">Kartlı ve biyometrik geçiş sistemleri</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-orange-600/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Tüm Altyapı ve Güvenlik Sistem Projeleri için Teklif Alın
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Uzman ekibimiz 24 saat içinde size ulaşır
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-3">
                  <i className="ri-search-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-white">Ücretsiz Keşif</h3>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mb-3">
                  <i className="ri-timer-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-white">Hızlı Teklif</h3>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-600 rounded-full mb-3">
                  <i className="ri-customer-service-line text-2xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-white">Profesyonel Danışmanlık</h3>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://wa.me/905537165561"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-green-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-500/50 transition-all duration-300 hover:scale-105 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                WhatsApp ile İletişim
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
